import { inject } from "aurelia-dependency-injection";
import {
  Redirect,
  NavigationInstruction,
  PipelineStep,
  Next,
} from "aurelia-router";
import { Authentication } from "./authentication";

@inject(Authentication)
export class AuthorizeStep implements PipelineStep {
  constructor(private auth: Authentication) {}

  run(routingContext: NavigationInstruction, next: Next) {
    let isLoggedIn = this.auth.isAuthenticated();
    let loginRoute = this.auth.getLoginRoute();

    if (this.needsLogin(routingContext)) {
      if (!isLoggedIn) {
        // remember current location (for redirect after login)
        this.auth.setInitialUrl(window.location.href);
        // redirect to login route
        return next.cancel(new Redirect(loginRoute));
      }
      if (!this.accessAllowed(routingContext)) {
        let noAccessRoute = this.auth.getNoAccessRoute();
        return next.cancel(new Redirect(noAccessRoute));
      }
    } else if (
      isLoggedIn &&
      routingContext.getAllInstructions().some((i) => i.fragment === loginRoute)
    ) {
      let loginRedirect = this.auth.getLoginRedirect();
      return next.cancel(new Redirect(loginRedirect));
    }

    return next();
  }

  /**
   * returns true if at least one part of the current route needs
   * authentication, ie. has settings.auth=true
   */
  private needsLogin(routingContext: NavigationInstruction): boolean {
    return routingContext
      .getAllInstructions()
      .some((i) => i.config.settings && i.config.settings.auth);
  }

  /**
   * returns true iff each part of the current route has at least one of the
   * necessary rights fulfilled. That is. for each part of the route it's
   * config setting 'needsRightOneOf' array must have at least <i>one</i>
   * matching right (if set).
   */
  private accessAllowed(routingContext: NavigationInstruction): boolean {
    for (let n of routingContext.getAllInstructions()) {
      let neededRights =
        (n.config.settings && n.config.settings.needsRightOneOf) || [];
      if (neededRights.length > 0 && !this.auth.hasRightOneOf(neededRights))
        return false;
    }
    return true;
  }
}
